import React, { useState, useEffect } from 'react';
import {
  XCircleIcon,
  MapPinIcon,
  ClockIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  ShareIcon,
  CheckIcon,
  DocumentDuplicateIcon,
  InformationCircleIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';

const ServiceDetailsModal = ({ 
  service, 
  isOpen, 
  onClose,
  onGenerateShareLink 
}) => {
  const [shareLink, setShareLink] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [generatingLink, setGeneratingLink] = useState(false);

  // Update share link when service changes or modal opens
  useEffect(() => {
    if (service?.shareToken) {
      setShareLink(`${window.location.origin}/service/${service.shareToken}`);
    } else {
      setShareLink(null);
    }
    setLinkCopied(false); // Reset copied state when service changes
  }, [service?.shareToken, isOpen]);

  if (!isOpen || !service) return null;

  // Parse availability data (basic time slots)
  let availability = [];
  try {
    if (typeof service.availability === 'string') {
      availability = JSON.parse(service.availability);
    } else if (Array.isArray(service.availability)) {
      availability = service.availability;
    } else {
      availability = [];
    }
  } catch (e) {
    availability = [];
  }

  // Parse sessions data (advanced sessions)
  let sessions = [];
  try {
    if (typeof service.sessions === 'string') {
      sessions = JSON.parse(service.sessions);
    } else if (Array.isArray(service.sessions)) {
      sessions = service.sessions;
    } else {
      sessions = [];
    }
  } catch (e) {
    sessions = [];
  }

  // Determine which time slots to show
  const hasAdvancedSessions = service.enableSessions && sessions.length > 0;
  const hasBasicSlots = availability.length > 0;

  const handleGenerateShareLink = async () => {
    setGeneratingLink(true);
    try {
      const newShareLink = await onGenerateShareLink(service.id);
      setShareLink(newShareLink);
    } catch (error) {
      console.error('Failed to generate share link:', error);
    } finally {
      setGeneratingLink(false);
    }
  };

  const copyToClipboard = async () => {
    if (shareLink) {
      try {
        await navigator.clipboard.writeText(shareLink);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy link:', error);
      }
    }
  };

  const formatRate = (rate, rateType) => {
    const formattedRate = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(rate);

    switch (rateType?.toUpperCase()) {
      case 'PER_PERSON':
        return `${formattedRate}/person`;
      case 'HOURLY':
        return `${formattedRate}/hour`;
      case 'FIXED':
        return `${formattedRate} (fixed)`;
      default:
        return formattedRate;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Parse date string manually to avoid timezone issues
    // dateString format: "2025-09-30"
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-6 pt-6 pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{service.name}</h2>
                <p className="text-sm text-gray-500 mt-1">Service Details</p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Photo Section */}
            {(service.photo || service.photoUrls?.original) && (
              <div className="mb-6">
                <img
                  src={service.photo || service.photoUrls?.original}
                  alt={service.name}
                  className="w-full h-64 object-cover rounded-lg"
                />
              </div>
            )}

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Basic Info */}
              <div className="lg:col-span-2 space-y-6">
                {/* Description */}
                {service.description && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                    <p className="text-gray-700 leading-relaxed">{service.description}</p>
                  </div>
                )}

                {/* Location */}
                {service.location && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                      <MapPinIcon className="h-5 w-5 mr-2" />
                      Location
                    </h3>
                    <p className="text-gray-700">{service.location}</p>
                  </div>
                )}

                {/* Organizer Notes */}
                {service.notes && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                      <InformationCircleIcon className="h-5 w-5 mr-2" />
                      Important Information
                    </h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-blue-800 leading-relaxed">{service.notes}</p>
                    </div>
                  </div>
                )}

                {/* Time Slots - Shows either Basic Availability or Advanced Sessions */}
                {hasAdvancedSessions ? (
                  // Show Advanced Sessions
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <CalendarDaysIcon className="h-5 w-5 mr-2" />
                      Available Sessions
                    </h3>
                    <div className="grid grid-cols-1 gap-4">
                      {sessions.map((session, index) => (
                        <div key={index} className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                          <div className="space-y-3">
                            {/* Session Name */}
                            {session.name && (
                              <div className="flex items-center text-lg font-medium text-blue-900">
                                <span>{session.name}</span>
                              </div>
                            )}

                            {/* Session Date */}
                            {session.date && (
                              <div className="flex items-center text-sm text-gray-700">
                                <CalendarDaysIcon className="h-4 w-4 mr-2 text-gray-500" />
                                <span className="font-medium">{formatDate(session.date)}</span>
                              </div>
                            )}

                            {/* Session Time Range */}
                            {(session.startTime || session.endTime) && (
                              <div className="flex items-center text-sm text-gray-700">
                                <ClockIcon className="h-4 w-4 mr-2 text-gray-500" />
                                <span>
                                  {session.startTime && formatTime(session.startTime)}
                                  {session.startTime && session.endTime && ' - '}
                                  {session.endTime && formatTime(session.endTime)}
                                </span>
                              </div>
                            )}

                            {/* Duration */}
                            {session.duration && (
                              <div className="flex items-center text-sm text-gray-700">
                                <ClockIcon className="h-4 w-4 mr-2 text-gray-500" />
                                <span>Duration: {session.duration} {session.duration === 1 ? 'hour' : 'hours'}</span>
                              </div>
                            )}

                            {/* Available Slots */}
                            {session.slotsAvailable && (
                              <div className="text-sm text-blue-700 font-medium">
                                {session.slotsAvailable} slot{session.slotsAvailable !== 1 ? 's' : ''} available
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : hasBasicSlots ? (
                  // Show Basic Availability Slots
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <CalendarDaysIcon className="h-5 w-5 mr-2" />
                      Available Time Slots
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {availability.map((slot, index) => (
                        <div key={index} className="border rounded-lg p-4 bg-gray-50">
                          <div className="space-y-2">
                            {slot.date && (
                              <div className="flex items-center text-sm">
                                <CalendarDaysIcon className="h-4 w-4 mr-2 text-gray-500" />
                                <span className="font-medium">{formatDate(slot.date)}</span>
                              </div>
                            )}
                            {(slot.startTime || slot.endTime) && (
                              <div className="flex items-center text-sm">
                                <ClockIcon className="h-4 w-4 mr-2 text-gray-500" />
                                <span>
                                  {slot.startTime && formatTime(slot.startTime)}
                                  {slot.startTime && slot.endTime && ' - '}
                                  {slot.endTime && formatTime(slot.endTime)}
                                </span>
                              </div>
                            )}
                            {slot.slotsAvailable && (
                              <div className="text-sm text-gray-600">
                                {slot.slotsAvailable} slot{slot.slotsAvailable !== 1 ? 's' : ''} available
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {/* Payment Methods */}
                {service.showPaymentOptions && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                      <CreditCardIcon className="h-5 w-5 mr-2" />
                      Payment Methods
                    </h3>
                    {(() => {
                      try {
                        // Try paymentMethods first (from parsed data), then paymentSettings (from raw data)
                        let paymentMethods = service.paymentMethods;
                        
                        if (!paymentMethods && service.paymentSettings) {
                          if (typeof service.paymentSettings === 'string') {
                            paymentMethods = JSON.parse(service.paymentSettings);
                          } else {
                            paymentMethods = service.paymentSettings;
                          }
                        }
                        
                        if (!paymentMethods || typeof paymentMethods !== 'object') {
                          return (
                            <div className="text-sm text-gray-600 italic">
                              No payment methods configured
                            </div>
                          );
                        }
                        
                        const hasPaymentMethods = Object.keys(paymentMethods).some(key => paymentMethods[key]);
                        
                        if (!hasPaymentMethods) {
                          return (
                            <div className="text-sm text-gray-600 italic">
                              No payment methods configured
                            </div>
                          );
                        }

                        return (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {paymentMethods.venmo && (
                              <div className="flex items-center p-3 bg-blue-50 rounded border">
                                <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                                <div>
                                  <div className="font-medium text-sm">Venmo</div>
                                  <div className="text-sm text-gray-600">{paymentMethods.venmo}</div>
                                </div>
                              </div>
                            )}
                            {paymentMethods.cashApp && (
                              <div className="flex items-center p-3 bg-green-50 rounded border">
                                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                                <div>
                                  <div className="font-medium text-sm">Cash App</div>
                                  <div className="text-sm text-gray-600">{paymentMethods.cashApp}</div>
                                </div>
                              </div>
                            )}
                            {paymentMethods.paypal && (
                              <div className="flex items-center p-3 bg-blue-50 rounded border">
                                <div className="w-3 h-3 bg-blue-600 rounded-full mr-3"></div>
                                <div>
                                  <div className="font-medium text-sm">PayPal</div>
                                  <div className="text-sm text-gray-600">{paymentMethods.paypal}</div>
                                </div>
                              </div>
                            )}
                            {paymentMethods.zelle && (
                              <div className="flex items-center p-3 bg-purple-50 rounded border">
                                <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                                <div>
                                  <div className="font-medium text-sm">Zelle</div>
                                  <div className="text-sm text-gray-600">{paymentMethods.zelle}</div>
                                </div>
                              </div>
                            )}
                            {paymentMethods.applePay && (
                              <div className="flex items-center p-3 bg-gray-50 rounded border">
                                <div className="w-3 h-3 bg-gray-800 rounded-full mr-3"></div>
                                <div>
                                  <div className="font-medium text-sm">Apple Pay</div>
                                  <div className="text-sm text-gray-600">{paymentMethods.applePay}</div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      } catch (error) {
                        return (
                          <div className="text-sm text-gray-600 italic">
                            Error loading payment methods
                          </div>
                        );
                      }
                    })()}
                  </div>
                )}
              </div>

              {/* Right Column - Pricing & Sharing */}
              <div className="space-y-6">
                {/* Pricing */}
                <div className="bg-purple-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 flex items-center">
                    <CurrencyDollarIcon className="h-5 w-5 mr-2" />
                    Pricing
                  </h3>
                  <div className="text-2xl font-bold text-purple-600">
                    {formatRate(service.rate, service.rateType)}
                  </div>

                  {service.showPaymentOptions && (
                    <div className="mt-2 text-sm text-gray-600">
                      Payment options available
                    </div>
                  )}
                </div>


                {/* Share Section */}
                <div className="bg-green-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <ShareIcon className="h-5 w-5 mr-2" />
                    Share This Service
                  </h3>
                  
                  {shareLink ? (
                    <div className="space-y-3">
                      <div className="bg-white rounded border p-3">
                        <div className="text-xs text-gray-500 mb-1">Share Link:</div>
                        <div className="text-sm break-all text-gray-800">{shareLink}</div>
                      </div>
                      <button
                        onClick={copyToClipboard}
                        className="w-full flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
                      >
                        {linkCopied ? (
                          <>
                            <CheckIcon className="h-4 w-4 mr-2" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                            Copy Link
                          </>
                        )}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleGenerateShareLink}
                      disabled={generatingLink}
                      className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm rounded-lg transition-colors"
                    >
                      <ShareIcon className="h-4 w-4 mr-2" />
                      {generatingLink ? 'Generating...' : 'Generate Invite Link'}
                    </button>
                  )}
                  
                  <div className="mt-3 text-xs text-gray-500">
                    Share this link to invite people to view and book this service. 
                    The link will expire after the event date has passed.
                  </div>
                </div>

                {/* Service Info */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Service Information</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Created:</span>
                      <span className="text-gray-900">
                        {new Date(service.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Updated:</span>
                      <span className="text-gray-900">
                        {new Date(service.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className={`font-medium ${service.isActive ? 'text-green-600' : 'text-red-600'}`}>
                        {service.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetailsModal;